import React, { useState, useEffect, useRef } from 'react';
import { Header, SideMenu, Loader } from '../../components';
import { customStyles } from '../../Utils/utils';
import ReportCard from '../../components/ReportCard';
import '../../styled/pages/dashboard.scss';
import PatientMeasurements from '../patients/components/patientMeasurements';
import EchoMeasurements from '../patients/components/echoMeasurements';
import PatientRelatedReports from '../patients/components/RelatedReports';
import PatientReportImages from '../patients/components/patientReportImages';
import { PatientReportIndex } from '../patients/components/patientReport';
import PatientDistribution from '../patients/components/patientDistribution';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AppDispatch, RootState } from '../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import { getReportDataById } from '../../rtk-apis/reports/getReportDataById';
import { clearState as clearReportDataState } from '../../store/slices/reports/getReportDataById';
import { dismissReminder } from '../../rtk-apis/reports/dismissReminder';
import { getReportFromPatientIdApi } from '../../rtk-apis/reports/getReportFromPatientId';
import { logoutAndWipeBrowser, getHospitalId } from '../../Utils/authUtils';
import PatientAttachments from '../patients/components/patientAttachments';
import { getRelatedReportApi } from '../../rtk-apis/relatedReports/getRelatedReport';
import { getDistributionCountsApi } from '../../rtk-apis/hl7/getDistributionCounts';
import { repeatStudyCheckApi, RepeatStudyCheckResult } from '../../rtk-apis/reports/repeatStudyCheck';
import MedicareAlertBanner from '../../components/MedicareAlertBanner';
import { servicesManager } from '../../../../platform/app/src/App';
import WallMotionBullseye from '../reports/components/WallMotionBullseye';
import { useClinicFeatures } from '../../Utils/useClinicFeatures';
import MeasurementHistoryHover from '../express/components/MeasurementHistoryHover';
import { Box, Typography, Button } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const PatientReport = () => {
  const { uiNotificationService } = servicesManager.services;
  const { _id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  // Per-clinic feature control (Super Admin → Feature control). The DICOM tab is
  // a viewer entry point — it navigates straight to /viewer — so it goes with the
  // viewer flag, while EXPRESS stays available to every clinic.
  const { dicomViewerEnabled, measurementEnabled } = useClinicFeatures();
  const [SelectedTab, setSelectedTab] = useState(1);
  const [ReportData, setReportData] = useState({});
  const [HitGetReportApi, setHitGetReportApi] = useState(false);
  const [getRelatedReportResponseCount, setGetRelatedReportResponseCount] = useState(0);
  const [distributionCount, setDistributionCount] = useState(0);
  const [isTemplateValid, setIsTemplateValid] = useState(true);
  const [repeatCheckResult, setRepeatCheckResult] = useState<RepeatStudyCheckResult | null>(null);
  // "Remember me" persists the whole login context in localStorage, while a
  // regular login uses sessionStorage. Reading sessionStorage directly caused
  // this page to silently skip getReportDataById for remembered sessions.
  const hospitalId = getHospitalId();

  const { status, data, msg, loading } = useSelector(
    (state: RootState) => state.getReportDataByIdResponse
  );
  const relatedReportsResponse = useSelector(
    (state: RootState) => state.getReportFromPatientIdResponse
  );
  const getRelatedReportResponse = useSelector(
    (state: RootState) => state.getRelatedReportResponse
  );

  useEffect(() => {
    if (Object.keys(ReportData).length > 0) {
      const payload = {
        // uhid: _id,
        studyInstanceUID: ReportData?.data?.studyInstanceUID,
        hospitalId: getHospitalId(),
        limit: 10,
        page: 1,
      };
      dispatch(getRelatedReportApi(payload));
    }
  }, [ReportData?.data?.studyInstanceUID]);

  useEffect(() => {
    const uid = ReportData?.data?.studyInstanceUID;
    if (!uid) return;
    dispatch(getDistributionCountsApi({ studyInstanceUIDs: [uid] })).then((action: any) => {
      if (getDistributionCountsApi.fulfilled.match(action)) {
        setDistributionCount(action.payload?.[uid]?.count ?? 0);
      }
    });
  }, [ReportData?.data?.studyInstanceUID]);

  // Medicare/MBS soft alert: prior echo / stress echo within 24 months (cross-clinic)
  useEffect(() => {
    const reportId = ReportData?.data?._id;
    if (!reportId) return;
    dispatch(repeatStudyCheckApi({ reportId })).then((action: any) => {
      if (repeatStudyCheckApi.fulfilled.match(action)) {
        setRepeatCheckResult(action.payload);
      }
    });
  }, [ReportData?.data?._id]);

  useEffect(() => {
    if (getRelatedReportResponse.status === 'SUCCESS') {
      setGetRelatedReportResponseCount(getRelatedReportResponse?.result?.length);
    }
  }, [getRelatedReportResponse]);

  // _id must be a dep: navigating notification→report (or report→report) changes
  // only the route param — the component stays mounted, so without it the page
  // kept showing the previous patient. Clear the old report first so the editor
  // never renders stale content while the new fetch is in flight.
  const lastFetchedIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (_id && lastFetchedIdRef.current && lastFetchedIdRef.current !== _id) {
      dispatch(clearReportDataState());
      setReportData({});
    }
    lastFetchedIdRef.current = _id;
    getReportData();
  }, [HitGetReportApi, _id]);

  // On SPA navigation the slice still holds the PREVIOUS report — the page then
  // renders stale content into the editor before the fresh fetch lands, and the
  // stale→fresh swap is what triggered the blank-editor wipe. Start clean instead.
  useEffect(() => {
    return () => {
      dispatch(clearReportDataState());
    };
  }, [dispatch]);

  const getReportData = () => {
    if (_id && hospitalId) {
      const payload = {
        reportId: _id,
        hospitalId,
      };
      dispatch(getReportDataById(payload));
      setHitGetReportApi(false);
    }
  };

  useEffect(() => {
    if (Object.keys(data).length > 0) {
      setReportData(data);
      const hospitalHeader = String(data?.hospitalHeader || "").trim();
      const hospitalLogo = String(data?.hospitalLogo || "").trim();
      const isValid = hospitalHeader && hospitalLogo;
      setIsTemplateValid(isValid);
      if (!isValid) {
        uiNotificationService.show({
          title: 'Report Template Error',
          message: 'Report template is not updated',
          type: 'error',
        });
      }

      // Auto-dismiss reminder when doctor opens a reminded report
      if (data?.data?.pendingReview) {
        dispatch(dismissReminder({ reportId: data.data._id }));
      }
    }
  }, [data, dispatch]);

  const tabs = [
    { tabNo: 1, label: 'Report' },
    { tabNo: 3, label: 'Related Reports' },
    { tabNo: 4, label: 'Attachments' },
    { tabNo: 5, label: 'Measurements' },
    // Echo Measurements + Wall Motion belong to the measurement module — hidden
    // unless the clinic has it AND the release gate is on.
    ...(measurementEnabled
      ? [
          { tabNo: 7, label: 'Echo Measurements' },
          { tabNo: 8, label: 'Wall Motion' },
        ]
      : []),
    // The DICOM tab opens the full viewer, so it follows the viewer flag.
    ...(dicomViewerEnabled ? [{ tabNo: 2, label: 'DICOM' }] : []),
    // { tabNo: 5, label: 'Distribution' },
  ];

  const [updateTemplateContent, setUpdatedTemplateContent] = useState({});

  // Create a ref to access Child 1's method
  const child1Ref = useRef(null);
  // Report body the measurement history card listens on (same card as Express).
  const [reportPanel, setReportPanel] = useState<HTMLElement | null>(null);

  // Saves pending editor changes before the server renders the PDF.
  const flushReportEdits = async (): Promise<boolean> =>
    (await child1Ref?.current?.flushPendingChanges?.()) ?? true;

  const [relatedReports, setRelatedReports] = useState([]);

  const handleImagesTabClick = () => {
    const studyData = {
      date: ReportData?.data?.studyDate,
      instances: ReportData?.data?.numberOfImages,
      modalities: ReportData?.data?.modality,
      mrn: ReportData?.data?.patientId,
      patientName: ReportData?.data?.name,
      studyInstanceUid: ReportData?.data?.studyInstanceUID,
      time: ReportData?.data?.studyTime,
      reportId: ReportData?.data?._id,
    };

    navigate(`/express/${ReportData?.data?.studyInstanceUID}`, { state: studyData });
  };

  const handleStressTabClick = () => {
    const studyData = {
      date: ReportData?.data?.studyDate,
      instances: ReportData?.data?.numberOfImages,
      modalities: ReportData?.data?.modality,
      mrn: ReportData?.data?.patientId,
      patientName: ReportData?.data?.name,
      studyInstanceUid: ReportData?.data?.studyInstanceUID,
      time: ReportData?.data?.studyTime,
      reportId: ReportData?.data?._id,
      isStressMode: true,
    };

    navigate(`/express/${ReportData?.data?.studyInstanceUID}`, { state: studyData });
  };

  useEffect(() => {
    if (Object.keys(ReportData).length > 0) {
      const payload = {
        hospitalId: getHospitalId(),
        patientId: ReportData?.data?.patientId,
      };
      dispatch(getReportFromPatientIdApi(payload));
    }
  }, [ReportData?.data?.patientId]);

  useEffect(() => {
    if (relatedReportsResponse.status === 'SUCCESS') {
      setRelatedReports(relatedReportsResponse.result.filter(report => report._id !== _id));
    }
  }, [relatedReportsResponse]);

  // If the report failed to load (e.g. a just-expired token that even the retry
  // couldn't recover, or a genuinely missing report), show a clear error state
  // instead of rendering the editor shell with undefined patient + blank body.
  const isMissingReportId = !_id;
  const isMissingHospitalContext = !hospitalId;
  const isMissingReportContext = isMissingReportId || isMissingHospitalContext;
  const reportLoadMessage = isMissingReportId
    ? 'This report link is incomplete. Please return to the report list and open it again.'
    : isMissingHospitalContext
      ? 'Your login is missing its clinic context. Please sign in again.'
    : typeof msg === 'string' && msg
      ? msg
      : 'The report could not be loaded. Your session may have expired — please retry or return to the report list.';
  if ((status === 'FAILED' && !loading) || isMissingReportContext) {
    return (
      <section className="layoutContainer" style={customStyles.layoutBackground}>
        <div className="layoutGrid">
          <div className="page">
            <Header />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                minHeight: '60vh',
                textAlign: 'center',
                px: 2,
              }}
            >
              <ErrorOutlineIcon sx={{ fontSize: 48, color: '#fb7185' }} />
              <Typography variant="h6" sx={{ color: '#e9f0f8' }}>
                Couldn’t load this report
              </Typography>
              <Typography variant="body2" sx={{ color: '#9fb2cc', maxWidth: 440 }}>
                {reportLoadMessage}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    if (isMissingHospitalContext) {
                      void logoutAndWipeBrowser(dispatch);
                      return;
                    }
                    if (isMissingReportId) {
                      navigate('/reports');
                      return;
                    }
                    dispatch(clearReportDataState());
                    if (_id && hospitalId) {
                      dispatch(getReportDataById({ reportId: _id, hospitalId }));
                    }
                  }}
                >
                  {isMissingHospitalContext
                    ? 'Sign in again'
                    : isMissingReportId
                      ? 'Back to Reports'
                      : 'Retry'}
                </Button>
                {!isMissingReportId && (
                  <Button variant="contained" onClick={() => navigate('/reports')}>
                    Back to Reports
                  </Button>
                )}
              </Box>
            </Box>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className="layoutContainer"
      style={customStyles.layoutBackground}
    >
      <div className="layoutGrid">
        <div className="page">
          <Header />
          <div className="page__reportBody" style={{ borderTop: '1px solid #fff' }}>
            <MedicareAlertBanner result={repeatCheckResult} />
            <ReportCard
              ReportData={ReportData}
              setHitGetReportApi={setHitGetReportApi}
              flushReportEdits={flushReportEdits}
              selectedTab={SelectedTab}
              updateTemplateContent={updateTemplateContent}
              isTemplateValid={isTemplateValid}
            />
            <div className="tabsWrap mt-2 p-6">
              <ul className="tabs">
                {tabs.map(tab => (
                  <li
                    key={tab.tabNo}
                    className={`tabs__link ${SelectedTab === tab.tabNo ? 'tabs__link--selected' : ''
                    }`}
                    onClick={() => setSelectedTab(tab?.tabNo)}
                  >
                    {tab.label === 'DICOM' ? (
                      <>
                        {tab.label} <span className="ml-1">{ReportData?.data?.numberOfImages}</span>
                      </>
                    ) : tab.label === 'Related Reports' ? (
                      <>
                        {tab.label} <span className="ml-1">{relatedReports?.length}</span>
                      </>
                    ) : tab.label === 'Attachments' ? (
                      <>
                        {tab.label} <span className="ml-1">{getRelatedReportResponseCount}</span>
                      </>
                    ) : (
                      <>{tab.label}</>
                    )}
                  </li>
                ))}
                <li
                  className="tabs__link"
                  onClick={handleImagesTabClick}
                >
                  EXPRESS
                </li>
                {measurementEnabled && ReportData?.data?.studyInstanceUID && (
                  <li
                    className="tabs__link"
                    onClick={() =>
                      navigate(`/measurement?StudyInstanceUIDs=${ReportData.data.studyInstanceUID}`)
                    }
                  >
                    MEASURE
                  </li>
                )}
                {ReportData?.data?.reportType === 'Stress Echo' && (
                  <li
                    className="tabs__link"
                    onClick={handleStressTabClick}
                  >
                    STRESS
                  </li>
                )}
                {ReportData?.data?.studyInstanceUID && (
                  <li
                    className="tabs__link"
                    onClick={() =>
                      navigate(`/distribution/${ReportData.data.studyInstanceUID}`, {
                        state: {
                          reportId: ReportData.data?._id,
                          studyData: {
                            name: ReportData.data?.name,
                            patientName: ReportData.data?.name,
                            dob: ReportData.data?.dob,
                            sex: ReportData.data?.sex,
                            age: ReportData.data?.age,
                            patientId: ReportData.data?.patientId,
                            reportName: ReportData.data?.reportName,
                            date: ReportData.data?.date,
                            time: ReportData.data?.time,
                            studyDate: ReportData.data?.studyDate,
                            studyTime: ReportData.data?.studyTime,
                            modality: ReportData.data?.modality,
                            reportAssignedTo: ReportData.data?.reportAssignedTo,
                            status: ReportData.data?.status,
                          },
                        },
                      })
                    }
                  >
                    DISTRIBUTE <span className="ml-1">{distributionCount}</span>
                  </li>
                )}
              </ul>
            </div>
            <div className="tabsContent mt-2 p-6">
              {SelectedTab === 1 ? (
                ReportData?.data?._id ? (
                  <div ref={setReportPanel}>
                    <PatientReportIndex
                      reportData={ReportData}
                      ref={child1Ref}
                      setHitGetReportApi={setHitGetReportApi}
                      // printRef={printRef}
                      setUpdatedTemplateContent={setUpdatedTemplateContent}
                    />
                    <MeasurementHistoryHover
                      panel={reportPanel}
                      patientId={(ReportData as any).data?.patientId}
                      sex={(ReportData as any).data?.sex}
                    />
                  </div>
                ) : null
              ) : SelectedTab === 2 ? (
                <PatientReportImages
                  ReportData={ReportData}
                  setSelectedTab={setSelectedTab}
                />
              ) : SelectedTab === 3 ? (
                <PatientRelatedReports relatedReports={relatedReports} />
              ) : SelectedTab === 4 ? (
                <PatientAttachments
                  ReportData={ReportData}
                  setGetRelatedReportResponseCount={setGetRelatedReportResponseCount}
                />
              ) : SelectedTab === 5 ? (
                <PatientMeasurements
                  ReportData={ReportData?.data}
                  setHitGetReportApi={setHitGetReportApi}
                />
              ) : SelectedTab === 7 ? (
                <EchoMeasurements ReportData={ReportData?.data} />
              ) : SelectedTab === 8 ? (
                <WallMotionBullseye studyInstanceUID={ReportData?.data?.studyInstanceUID} />
              ) : SelectedTab === 6 ? (
                <PatientDistribution
                  patientName={ReportData?.name}
                  patientDob={ReportData?.dob}
                  reportType={ReportData?.reportType}
                  reportDate={ReportData?.date}
                  reportTime={ReportData?.time}
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>
      {/* Real fetch-driven loader — masks the stale/blank flash while the report loads. */}
      <Loader isLoading={loading} />
    </section>
  );
};
export default PatientReport;
